import { Editor, TLShapeId, TLSvgOptions, getSvgAsImage, Box2d } from '@tldraw/tldraw'
import { getOrderedFrames } from 'components/VisualStudioPlayground/Shapes/FrameShape/helpers'
import JSZip from 'jszip'
import { downloadVideo } from './downloadFile'
import { exportAs } from './exportAs'
import { FrameShape } from 'components/VisualStudioPlayground/Shapes/FrameShape/Types'
import { FrameShapeUtil } from 'components/VisualStudioPlayground/Shapes/FrameShape/FrameShapeUtil'
import { getVideo } from '../../helpers'
import { exportVideoFrame } from './exportDesignVideo'

export type ExportFormat = 'png' | 'svg' | 'jpeg' | 'webp'

export type ExportOptions = TLSvgOptions & { quality?: number }

type BaseFrame = { frameId: string; designKey: string }

export type ImageFrame = BaseFrame & { type: 'image'; file: File; mime_type: string }

export type VideoFrame = BaseFrame & {
  type: 'video'
  file: File
  mime_type: string
  source_url: string
  duration: number
}

export type ExportedFrame = ImageFrame | VideoFrame

async function getSvgAsString(svg: SVGElement) {
  const clone = svg.cloneNode(true) as SVGGraphicsElement

  const width = svg.getAttribute('width')
  const height = svg.getAttribute('height')

  if (!width || !height) {
    throw new Error('Could not get SVG width or height.')
  }

  svg.setAttribute('width', `${width}`)
  svg.setAttribute('height', `${height}`)

  const fileReader = new FileReader()
  const imgs = Array.from(clone.querySelectorAll('image')) as SVGImageElement[]

  for (const img of imgs) {
    const src = img.getAttribute('xlink:href')
    if (src) {
      if (!src.startsWith('data:')) {
        const blob = await (await fetch(src)).blob()
        const base64 = await new Promise<string>((resolve, reject) => {
          fileReader.onload = () => resolve(fileReader.result as string)
          fileReader.onerror = () => reject(fileReader.error)
          fileReader.readAsDataURL(blob)
        })
        img.setAttribute('xlink:href', base64)
      }
    }
  }

  const out = new XMLSerializer()
    .serializeToString(clone)
    .replaceAll('&#10;      ', '')
    .replaceAll(/((\s|")[0-9]*\.[0-9]{2})([0-9]*)(\b|"|\))/g, '$1')

  return out
}

function wrapFrameSvg(frameSvgG: SVGGElement, bounds: Box2d, defsHtmlString: string) {
  const rect = frameSvgG.querySelector('rect')
  const width = frameSvgG.getAttribute('width') || rect?.getAttribute('width') || bounds.width.toString()
  const height = frameSvgG.getAttribute('height') || rect?.getAttribute('height') || bounds.width.toString()
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')

  if (defsHtmlString.length) {
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
    defs.innerHTML = defsHtmlString
    svg.appendChild(defs)
  }
  svg.appendChild(frameSvgG)
  svg.setAttribute('width', width)
  svg.setAttribute('height', height)
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`)

  return svg
}

export async function getSvg(editor: Editor, ids: TLShapeId[], opts: Partial<TLSvgOptions>, addCoords = false) {
  const frameId = ids.find((id) => editor.getShape(id)?.type === 'frame')
  let frameSvgG: SVGGElement | null = null
  let idsToExport = ids
  let defsToAdd = ''

  if (frameId) {
    const shape = editor.getShape(frameId)
    if (shape) {
      const frame = shape as FrameShape
      const frameUtil = editor.getShapeUtil('frame') as FrameShapeUtil
      frameSvgG = await frameUtil.toSvg(
        frame,
        { addExportDef: () => {} },
        (defsHtmlString: string) => {
          defsToAdd = defsHtmlString
        },
        addCoords,
      )
      idsToExport = ids.filter((id) => id !== frameId)

      if (idsToExport.length === 0) {
        return wrapFrameSvg(frameSvgG, opts.bounds || editor.getShapeGeometry(frame).bounds, defsToAdd)
      }
    }
  }

  const svg = await editor.getSvg(idsToExport?.length ? idsToExport : [...editor.getCurrentPageShapeIds()], opts)

  if (!svg) {
    throw new Error('Could not construct SVG.')
  }

  if (frameSvgG) {
    svg.insertBefore(frameSvgG, svg.firstChild)

    if (defsToAdd.length) {
      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
      defs.innerHTML = defsToAdd
      svg.insertBefore(defs, svg.firstChild)
    }
  }

  if (opts.background === false) {
    svg.querySelectorAll('rect[data-shape-type="frame"]').forEach((rect) => {
      rect.setAttribute('fill', 'transparent')
    })
  }

  return svg
}

export async function exportToBlob(
  editor: Editor,
  ids: TLShapeId[],
  format: ExportFormat,
  opts = {} as Partial<ExportOptions>,
): Promise<Blob> {
  const { bounds, padding = 0, background = editor.getInstanceState().exportBackground, quality = 1, scale = 1 } = opts

  switch (format) {
    case 'svg':
      return new Blob([await getSvgAsString(await getSvg(editor, ids, { background, bounds, padding }))], {
        type: 'text/plain',
      })
    case 'webp':
    case 'jpeg':
    case 'png': {
      const image = await getSvgAsImage(
        await getSvg(editor, ids, { background, bounds, padding }, true),
        editor.environment.isSafari,
        {
          type: format,
          quality,
          scale,
        },
      )
      if (!image) {
        throw new Error('Could not construct image.')
      }
      return image
    }
  }
}

function exportStaticFrame(editor: Editor, frameId: TLShapeId, format: ExportFormat, opts: Partial<ExportOptions>) {
  return exportAs(editor, [frameId, ...editor.getSortedChildIdsForParent(frameId)], format, opts)
}

export async function exportDesignToFiles(
  editor: Editor,
  fileId: string,
  format: ExportFormat,
  opts = {} as Partial<ExportOptions>,
): Promise<ExportedFrame[]> {
  const frames = getOrderedFrames(editor)
  const filesArray = await Promise.all(frames.map(async (frame) => frameToFile(editor, fileId, format, opts, frame)))
  return filesArray
}

export async function frameToFile(
  editor: Editor,
  fileId: string,
  format: ExportFormat,
  opts = {} as Partial<ExportOptions>,
  frame: FrameShape,
): Promise<ExportedFrame> {
  const pageBounds = editor.getShapePageBounds(frame)
  const frameBounds = editor.getShapeGeometry(frame).bounds
  const bounds = new Box2d(
    pageBounds?.x || frameBounds.x,
    pageBounds?.y || frameBounds.y,
    frameBounds.width,
    frameBounds.height,
  )
  const videoShape = getVideo(editor, frame.id)
  if (!!videoShape) {
    const video = await exportVideoFrame(editor, fileId, frame.id, bounds)
    const thumbnail = await exportStaticFrame(editor, frame.id, format, { ...opts, bounds })
    return {
      type: 'video',
      frameId: frame.id.split(':')[1],
      mime_type: video.mimeType,
      source_url: video.url,
      file: thumbnail,
      duration: videoShape.props.duration,
      designKey: fileId,
    } as VideoFrame
  } else {
    return {
      type: 'image',
      frameId: frame.id.split(':')[1],
      designKey: fileId,
      file: await exportStaticFrame(editor, frame.id, format, { ...opts, bounds }),
    } as ImageFrame
  }
}

export async function zipDesignFiles(files: File[], zipTitle?: string) {
  const zip = new JSZip()
  files.forEach((file, index) => {
    zip.file(`${index + 1} - ${file.name}`, file)
  })
  const content = await zip.generateAsync({ type: 'blob' })
  return new File([content], `${zipTitle}.zip` || 'design.zip')
}

export async function generateFiles(designFiles: ExportedFrame[]) {
  let files: File[] = []
  for (const frame of designFiles.values()) {
    if (frame.type === 'video') {
      const video = await downloadVideo(frame)

      files.push(video)
    } else {
      files.push(frame.file)
    }
  }

  return files
}

export async function exportDesign(
  editor: Editor,
  fileId: string,
  format: ExportFormat,
  opts = {} as Partial<ExportOptions>,
  zipFiles = true,
): Promise<File[]> {
  const designFiles = await exportDesignToFiles(editor, fileId, format, opts)
  if (designFiles.length === 1) {
    if (designFiles[0].type === 'video') {
      const video = await downloadVideo(designFiles[0])
      return [video]
    } else {
      return [designFiles[0].file]
    }
  } else {
    const files = await generateFiles(designFiles)
    if (zipFiles) {
      return [await zipDesignFiles(files)]
    } else {
      return files
    }
  }
}
